import React, { useState, useEffect } from 'react';
import { FaQuestionCircle, FaEnvelope, FaHeart, FaPaypal } from 'react-icons/fa';
import AdUnit from './AdSense';
import emailjs from '@emailjs/browser';

const HelpSupport = () => {
    const [activeSection, setActiveSection] = useState('faq');
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        message: ''
    });
    const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });

    useEffect(() => {
        // Load PayPal script
        const script = document.createElement('script');
        script.src = 'https://www.paypal.com/sdk/js?client-id=test&currency=USD';
        script.async = true;
        document.body.appendChild(script);

        return () => {
            document.body.removeChild(script);
        };
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitStatus({ type: 'loading', message: 'Sending message...' });

        try {
            await emailjs.send(
                process.env.REACT_APP_EMAILJS_SERVICE_ID,
                process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
                {
                    from_name: formData.name,
                    from_email: formData.email,
                    message: formData.message,
                    to_email: process.env.REACT_APP_CONTACT_EMAIL
                },
                process.env.REACT_APP_EMAILJS_PUBLIC_KEY
            );

            setSubmitStatus({ type: 'success', message: 'Message sent successfully!' });
            setFormData({ name: '', email: '', message: '' });
        } catch (error) {
            console.error('Error sending email:', error);
            setSubmitStatus({ type: 'error', message: 'Failed to send message. Please try again.' });
        }
    };

    const faqs = [
        {
            question: "What is Dreamarium?",
            answer: "Dreamarium is a social platform for sharing and interpreting dreams, combining modern AI technology with astrological insights to help users understand their subconscious mind."
        },
        {
            question: "How does dream interpretation work?",
            answer: "We use advanced AI to analyze your dreams through an astrological lens, providing personalized insights about symbolism, celestial influences, and potential meanings."
        },
        {
            question: "Is my dream data private?",
            answer: "Yes! While you can choose to share dreams publicly, all dreams are private by default. We use industry-standard security measures to protect your data."
        },
        {
            question: "Can I delete my account?",
            answer: "Yes, you can delete your account and all associated data at any time through your profile settings."
        },
        {
            question: "How can I connect with other dreamers?",
            answer: "You can follow other users, like and comment on their dreams, and share your own experiences. Our network feature helps you find dreamers with similar interests."
        }
    ];

    return (
        <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-black text-white">
            <div className="max-w-4xl mx-auto px-4 py-8">
                <AdUnit adSlot="6045190321" />
                <h1 className="text-3xl font-bold text-center mb-8 bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-indigo-400">
                    Help & Support
                </h1>

                <div className="flex flex-wrap justify-center mb-8 gap-4">
                    <button
                        onClick={() => setActiveSection('faq')}
                        className={`flex items-center px-4 py-2 rounded-full transition-all hover:scale-105 ${
                            activeSection === 'faq' 
                                ? 'bg-gradient-to-r from-purple-500 to-indigo-500 text-white' 
                                : 'bg-white/10 text-white hover:bg-white/20'
                        }`}
                    >
                        <FaQuestionCircle className="mr-2" />
                        FAQ
                    </button>
                    <button
                        onClick={() => setActiveSection('contact')}
                        className={`flex items-center px-4 py-2 rounded-full transition-all hover:scale-105 ${
                            activeSection === 'contact' 
                                ? 'bg-gradient-to-r from-purple-500 to-indigo-500 text-white' 
                                : 'bg-white/10 text-white hover:bg-white/20'
                        }`}
                    >
                        <FaEnvelope className="mr-2" />
                        Contact
                    </button>
                    <button
                        onClick={() => setActiveSection('donate')}
                        className={`flex items-center px-4 py-2 rounded-full transition-all hover:scale-105 ${
                            activeSection === 'donate' 
                                ? 'bg-gradient-to-r from-purple-500 to-indigo-500 text-white' 
                                : 'bg-white/10 text-white hover:bg-white/20'
                        }`}
                    >
                        <FaHeart className="mr-2" />
                        Donate
                    </button>
                </div>

                <div className="bg-white/5 backdrop-blur-lg rounded-xl p-6 border border-white/10">
                    {activeSection === 'faq' && (
                        <div className="space-y-6">
                            <h2 className="text-2xl font-semibold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-indigo-400">
                                Frequently Asked Questions
                            </h2>
                            {faqs.map((faq, index) => (
                                <div key={index} className="border-b border-white/10 pb-4">
                                    <h3 className="text-lg font-medium mb-2 text-white">
                                        {faq.question}
                                    </h3>
                                    <p className="text-gray-300">
                                        {faq.answer}
                                    </p>
                                </div>
                            ))}
                        </div>
                    )}

                    {activeSection === 'contact' && (
                        <div>
                            <h2 className="text-2xl font-semibold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-indigo-400">
                                Contact Us
                            </h2>
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-1">
                                        Name
                                    </label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                        placeholder="Your name"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-1">
                                        Email
                                    </label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                        placeholder="your@email.com"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-1">
                                        Message
                                    </label>
                                    <textarea
                                        name="message"
                                        value={formData.message}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent h-32"
                                        placeholder="How can we help?"
                                        required
                                    ></textarea>
                                </div>
                                {submitStatus.message && (
                                    <div className={`text-sm ${
                                        submitStatus.type === 'success' ? 'text-green-400' : 
                                        submitStatus.type === 'error' ? 'text-red-400' : 
                                        'text-gray-400'
                                    }`}>
                                        {submitStatus.message}
                                    </div>
                                )}
                                <button
                                    type="submit"
                                    className="w-full bg-gradient-to-r from-purple-500 to-indigo-500 text-white py-2 px-4 rounded-lg hover:from-purple-600 hover:to-indigo-600 transition-all hover:scale-[1.02]"
                                    disabled={submitStatus.type === 'loading'}
                                >
                                    {submitStatus.type === 'loading' ? 'Sending...' : 'Send Message'}
                                </button>
                            </form>
                        </div>
                    )}

                    {activeSection === 'donate' && (
                        <div>
                            <h2 className="text-2xl font-semibold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-indigo-400">
                                Support Dreamarium
                            </h2>
                            <p className="text-gray-300 mb-8">
                                Help us continue providing dream interpretation services and maintaining our platform.
                                Your support makes a difference!
                            </p>
                            
                            <div className="space-y-6">
                                {/* PayPal Buttons */}
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                    {[5, 10, 25].map((amount) => (
                                        <form key={amount} action="https://www.paypal.com/donate" method="post" target="_blank">
                                            <input type="hidden" name="business" value="landjester@gmail.com" />
                                            <input type="hidden" name="amount" value={amount} />
                                            <input type="hidden" name="currency_code" value="USD" />
                                            <button
                                                type="submit"
                                                className="w-full bg-[#0070BA] hover:bg-[#003087] text-white py-3 px-6 rounded-lg transition-all hover:scale-105 flex items-center justify-center"
                                            >
                                                <FaPaypal className="mr-2" />
                                                Donate ${amount}
                                            </button>
                                        </form>
                                    ))}
                                </div>

                                {/* Custom Amount */}
                                <div className="mt-6">
                                    <form action="https://www.paypal.com/donate" method="post" target="_blank">
                                        <input type="hidden" name="business" value="landjester@gmail.com" />
                                        <input type="hidden" name="currency_code" value="USD" />
                                        <button
                                            type="submit"
                                            className="w-full bg-gradient-to-r from-purple-500 to-indigo-500 text-white py-3 px-6 rounded-lg hover:from-purple-600 hover:to-indigo-600 transition-all hover:scale-105 flex items-center justify-center"
                                        >
                                            <FaPaypal className="mr-2" />
                                            Custom Amount
                                        </button>
                                    </form>
                                </div>

                                <p className="text-sm text-gray-400 mt-4 text-center">
                                    All donations are processed securely through PayPal
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default HelpSupport;
